import { type IconType } from '@src/IconType';
import Chip from '@components/Chip.tsx';
import { useCallback, useState } from 'react';
import { classNames } from '@utils/classNames';

export type ChipListProperties<T> = {
  readonly items: T[];
  readonly getLabel: (item: T) => string;
  readonly onChange: (items: T[]) => void;
  readonly canRemove?: (item: T) => boolean;
  readonly canEdit?: (item: T) => boolean;
  readonly editPanel?: (item: T, closePanel: () => void) => React.ReactNode;
  readonly loading?: boolean;
  readonly getIcon?: (item: T) => IconType | undefined;
  readonly getClassName?: (item: T) => string | undefined;
  readonly getDataTestId?: (item: T) => string;
};

export default function ChipList<T>({
  items,
  getLabel,
  onChange,
  canRemove,
  canEdit,
  editPanel,
  loading,
  getIcon,
  getClassName,
  getDataTestId,
}: ChipListProperties<T>) {
  const [editItem, setEditItem] = useState<number | null>(null);
  const removeItem = useCallback(
    (item: T) => {
      const otherItems = items.filter(
        (existing) => getLabel(existing) !== getLabel(item),
      );
      onChange(otherItems);
    },
    [items, onChange, getLabel],
  );

  const openItemEdit = useCallback(
    (index: number) => {
      if (editItem === index) {
        setEditItem(null);
      } else {
        setEditItem(index);
      }
    },
    [editItem],
  );

  return (
    <div
      className={classNames(
        'flex flex-row gap-3 mb-6',
        'flex-nowrap sm:flex-wrap overflow-x-auto', // allow horizontal scrolling on small screens
      )}
    >
      {items.map((item, index) => {
        const label = getLabel(item);
        return (
          <div
            className="relative"
            key={label}
            data-testid={
              'chip-item-' + (getDataTestId ? getDataTestId(item) : label)
            }
          >
            <Chip
              label={label}
              onRemove={
                !canRemove || canRemove(item)
                  ? () => removeItem(item)
                  : undefined
              }
              onEdit={
                canEdit && canEdit(item) && editPanel
                  ? () => openItemEdit(index)
                  : undefined
              }
              canEdit={canEdit && canEdit(item) && !!editPanel}
              loading={loading}
              icon={getIcon?.(item)}
              className={getClassName?.(item)}
              testIdKey={getDataTestId?.(item)}
            />
            {editItem === index && editPanel?.(item, () => setEditItem(null))}
          </div>
        );
      })}
    </div>
  );
}
